import {Datapoint, Sort} from '@deviceinsight/ng-ui-api-client';

import {useConfirm} from '@deviceinsight/ng-ui-basic-components';
import {messageBoxError, messageBoxSuccess} from '@deviceinsight/ng-ui-components';

import {TFunction, Trans} from '@deviceinsight/ng-ui-scale-lib/api';
import {
	DataProcessingDefinitionValidationError,
	KpiEditorDatapoint,
	KpiFormData,
	MissingDatapointsPerDPD
} from './types';
import {
	assignDataProcessingDefinitionsToAsset,
	assignDataProcessingDefinitionsToAssetGroup,
	DataProcessingDefinition,
	DataProcessingDefinitionBody,
	DataProcessingDefinitionDatapoint,
	DataProcessingDefinitionOutput,
	fetchDataProcessingDefinitionsAssignedToAsset,
	fetchDataProcessingDefinitionsAssignedToAssetGroup,
	updateDataProcessingDefinition
} from '../../api/dataProcessingDefinitionApi';

const getDateWithoutTime = () => {
	return new Date(new Date().getFullYear(), new Date().getMonth(), new Date().getDate());
};

const getAssignedKPIIdsForAsset = async (assetId: number) => {
	const assignedKPIs = await fetchDataProcessingDefinitionsAssignedToAsset(assetId);
	return assignedKPIs.filter(({directlyAssigned}) => directlyAssigned).map(({id}) => id);
};

const getAssignedKPIIdsForAssetGroup = async (assetGroupId: number) => {
	const assignedKPIs = await fetchDataProcessingDefinitionsAssignedToAssetGroup(assetGroupId);
	return assignedKPIs.map(({id}) => id);
};

export const assignDPDToAsset = async (definitionId: number, assetId: number) => {
	try {
		const assignedKPIIds = await getAssignedKPIIdsForAsset(assetId);
		const nextAssignedKPIs = [...assignedKPIIds, definitionId];
		await assignDataProcessingDefinitionsToAsset(assetId, nextAssignedKPIs);
	} catch (error) {
		displayErrorMessage(error);
		throw error;
	}
};

export const removeDPDFromAsset = async (definitionId: number, assetId: number) => {
	try {
		const assignedKPIIds = await getAssignedKPIIdsForAsset(assetId);
		const nextAssignedKPIs = assignedKPIIds.filter((id) => id !== definitionId);
		await assignDataProcessingDefinitionsToAsset(assetId, nextAssignedKPIs);
	} catch (error) {
		displayErrorMessage(error);
		throw error;
	}
};

export const removeDPDFromAssetGroup = async (definitionId: number, assetGroupId: number) => {
	try {
		const assignedKPIIds = await getAssignedKPIIdsForAssetGroup(assetGroupId);
		const nextAssignedKPIs = assignedKPIIds.filter((id) => id !== definitionId);
		await assignDataProcessingDefinitionsToAssetGroup(assetGroupId, nextAssignedKPIs);
	} catch (error) {
		displayErrorMessage(error);
		throw error;
	}
};

export const transformToKpiFormData = (
	dataProcessingDefinition: DataProcessingDefinition | null | undefined
): KpiFormData => {
	if (dataProcessingDefinition) {
		// existing definition
		const {definition} = dataProcessingDefinition;
		return {
			name: definition.kpiName,
			flexExpression: addSemicolonToLastLine(definition.expression),
			inputVariables: toFlexEditorDatapointDefinition(definition.input),
			outputVariables: toFlexEditorOutputVariables(definition.output),
			processingOptions: {
				period: definition.processingPeriod,
				startProcessingAt: getDateWithoutTime(),
				startProcessingAtHour: new Date().getHours(),
				ensureBoundaryValues: definition.ensureBoundaryValues
			},
			publishAsDatapointValues: !!definition.publishAsDatapointValues
		};
	} else {
		// new definition
		return {
			name: '',
			flexExpression: '',
			inputVariables: [],
			outputVariables: [],
			processingOptions: {
				period: 'INSTANT',
				startProcessingAt: getDateWithoutTime(),
				startProcessingAtHour: new Date().getHours(),
				ensureBoundaryValues: true
			},
			publishAsDatapointValues: true
		};
	}
};

const toFlexEditorDatapointDefinition = (
	definitionDatapoints: Array<DataProcessingDefinitionDatapoint>
): Array<KpiEditorDatapoint> => {
	return definitionDatapoints.map((dp) => {
		return {
			name: dp.variableName,
			datapoint: dp.datapointKey,
			type: dp.dataType,
			requiresValueInCurrentPeriod: dp.requiresValueInCurrentPeriod
		};
	});
};

const toFlexEditorOutputVariables = (outputVariables: Array<DataProcessingDefinitionOutput>): Array<string> => {
	return outputVariables.map((v) => v.variableName);
};

// the flex expression on the server does not allow a semicolon on the last line,
// but the flex-js-editor needs it
const addSemicolonToLastLine = (flexExpression: string): string => {
	if (flexExpression.endsWith(';')) {
		return flexExpression;
	} else {
		return flexExpression + ';';
	}
};

const toDataProcessingDefinitionDatapoint = (
	kpiEditorInputVariables: Array<KpiEditorDatapoint>
): Array<DataProcessingDefinitionDatapoint> => {
	return kpiEditorInputVariables.map((v) => {
		return {
			datapointKey: v.datapoint,
			variableName: v.name,
			dataType: v.type,
			requiresValueInCurrentPeriod: v.requiresValueInCurrentPeriod
		};
	}) as Array<DataProcessingDefinitionDatapoint>;
};

const toDataProcessingDefinitionOutput = (
	kpiEditorOutputVariables: Array<string>
): Array<DataProcessingDefinitionOutput> => {
	return kpiEditorOutputVariables.map((v) => {
		return {
			outputKey: 'dp.' + v,
			variableName: v
		};
	});
};

const trimAndRemoveSemicolonFromLastLine = (flexExpression: string): string => {
	const expression = flexExpression.trim();

	if (expression.endsWith(';')) {
		return expression.slice(0, -1);
	} else {
		return expression;
	}
};

export const getDefinitionBody = (kpiEditorDefinition: KpiFormData): DataProcessingDefinitionBody => {
	const processingDefType = 'expressionPD';
	const kpiName = kpiEditorDefinition.name;
	const processingPeriod = kpiEditorDefinition.processingOptions.period;
	const startProcessingAt = kpiEditorDefinition.processingOptions.startProcessingAt ?? new Date();
	const resetTo = new Date(
		startProcessingAt.getFullYear(),
		startProcessingAt.getMonth(),
		startProcessingAt.getDate(),
		kpiEditorDefinition.processingOptions.startProcessingAtHour
	).toISOString();
	const ensureBoundaryValues = kpiEditorDefinition.processingOptions.ensureBoundaryValues;
	const expression = trimAndRemoveSemicolonFromLastLine(kpiEditorDefinition.flexExpression);
	const input = toDataProcessingDefinitionDatapoint(kpiEditorDefinition.inputVariables);
	const output = toDataProcessingDefinitionOutput(kpiEditorDefinition.outputVariables);
	const publishAsDatapointValues = kpiEditorDefinition.publishAsDatapointValues;
	return {
		processingDefType,
		kpiName,
		processingPeriod,
		resetTo,
		ensureBoundaryValues,
		expression,
		input,
		output,
		publishAsDatapointValues
	};
};

export const getDuplicatedDefinitionBody = (orig: DataProcessingDefinition): DataProcessingDefinitionBody => {
	return {
		processingDefType: orig.definition.processingDefType,
		kpiName: `${orig.definition.kpiName}_duplicate`,
		processingPeriod: orig.definition.processingPeriod,
		resetTo: orig.definition.resetTo,
		ensureBoundaryValues: orig.definition.ensureBoundaryValues,
		expression: orig.definition.expression,
		input: orig.definition.input,
		output: orig.definition.output,
		publishAsDatapointValues: orig.definition.publishAsDatapointValues
	};
};

export const updateCurrentDataProcessingDefinitionFromKpiEditor = (
	kpiEditorDefinition: KpiFormData,
	currentDataProcessingDefinition: DataProcessingDefinition | null | undefined
): DataProcessingDefinition => {
	if (!currentDataProcessingDefinition) {
		throw new Error('currentDataProcessingDefinition is null when doing update');
	}

	return {
		...currentDataProcessingDefinition,
		definition: {
			...currentDataProcessingDefinition.definition,
			...getDefinitionBody(kpiEditorDefinition),
			// version increment will trigger recalculation in the backend
			version: (currentDataProcessingDefinition.definition.version ?? 0) + 1
		}
	};
};

export const mapToDataProcessingDefinitionDatapoint = (
	inputInKpiEditorDatapoint: Array<KpiEditorDatapoint>
): Array<DataProcessingDefinitionDatapoint> => {
	return inputInKpiEditorDatapoint.map((inputVariable: KpiEditorDatapoint) => ({
		variableName: inputVariable.name,
		datapointKey: inputVariable.datapoint,
		dataType: inputVariable.type
	})) as Array<DataProcessingDefinitionDatapoint>;
};

export const getDatapointsMissing = (
	neededDatapoints: Array<DataProcessingDefinitionDatapoint>,
	availableDatapoints: Array<Datapoint>
): Array<DataProcessingDefinitionDatapoint> => {
	return neededDatapoints.filter(
		(neededDatapoint) => !availableDatapoints.find((dp) => dp.name === neededDatapoint.datapointKey)
	);
};

export const getMissingDatapointsPerDPD = (
	dataProcessingDefinitions: Array<DataProcessingDefinition>,
	datapoints: Array<Datapoint>
): MissingDatapointsPerDPD => {
	const initialValue: Record<number, Array<string>> = {};
	return dataProcessingDefinitions.reduce((missingDPDs, dpd) => {
		const {
			id,
			definition: {input}
		} = dpd;
		missingDPDs[id] = getDatapointsMissing(input, datapoints).map((dp) => dp.datapointKey);
		return missingDPDs;
	}, initialValue);
};

export const sort = (
	items: Array<DataProcessingDefinition>,
	sort: Sort,
	defaultSortItems: (items: Array<DataProcessingDefinition>, sort: Sort) => Array<DataProcessingDefinition>
): Array<DataProcessingDefinition> => {
	const reverseFactor = sort.direction === 'ASC' ? 1 : -1;

	const getOutputKpi = (item: DataProcessingDefinition): string =>
		item.definition.output.map((dp) => dp.variableName).join(',');

	switch (sort.property) {
		case 'kpiName':
			return items.sort(
				(dpd1: DataProcessingDefinition, dpd2: DataProcessingDefinition) =>
					reverseFactor * dpd1.definition.kpiName.localeCompare(dpd2.definition.kpiName)
			);

		case 'outputKpi':
			return items.sort(
				(dpd1: DataProcessingDefinition, dpd2: DataProcessingDefinition) =>
					reverseFactor * getOutputKpi(dpd1).localeCompare(getOutputKpi(dpd2))
			);

		case 'processingPeriod':
			return items.sort(
				(dpd1: DataProcessingDefinition, dpd2: DataProcessingDefinition) =>
					reverseFactor * dpd1.definition.processingPeriod.localeCompare(dpd2.definition.processingPeriod)
			);

		default:
			return defaultSortItems(items, sort);
	}
};

export const displaySuccessMessage = (message: JSX.Element | string) => {
	messageBoxSuccess(message, undefined, 'DataProcessingDefinition');
};

export const displayErrorMessage = (error: any) => {
	const backendErrorMessage =
		error.response && error.response.data && error.response.data.message
			? error.response.data.message
			: error.message;
	const defaultErrorMessage = (
		<Trans i18nKey="assets.detail.dataProcessingDefinition.saveErrorMessage.default">
			An error occurred on save: {{backendErrorMessage}}
		</Trans>
	);
	const errorSaveKPIMessage = (
		<Trans i18nKey="assets.detail.dataProcessingDefinition.saveErrorMessage.saveKPI">
			The Processing Definition could not be saved. Please have a look at the marked issues below...
		</Trans>
	);
	messageBoxError(
		error ? defaultErrorMessage : errorSaveKPIMessage,
		{
			autoClose: false,
			bodyStyle: {height: 'auto'}
		},
		'DataProcessingDefinition'
	);
};

export const getUniqueKPIs = (definitions: Array<DataProcessingDefinition>): Array<DataProcessingDefinition> => {
	return definitions.filter(
		(def1) => !definitions.find((def2) => def1 !== def2 && def1.id === def2.id && def1.directlyAssigned)
	);
};

export const getDataProcessingDefinitionValidationErrors = (error: any): DataProcessingDefinitionValidationError[] =>
	error.response?.data?.dataProcessingDefinitionValidationErrors || [];

export const tryUpdateDpd = async (
	updatedDefinition: DataProcessingDefinition,
	confirm: ReturnType<typeof useConfirm>,
	t: TFunction
): Promise<boolean> => {
	try {
		await updateDataProcessingDefinition(updatedDefinition);
		return true;
	} catch (error: any) {
		if (error.response.status === 412) {
			const overwrite = await confirm({
				title: t('assets.detail.dataProcessingDefinition.overwriteConfirmTitle', {defaultValue: 'Warning'}),
				message: t('assets.detail.dataProcessingDefinition.overwriteConfirmMessage', {
					defaultValue:
						'This data processing definition has been updated by someone else. Do you want to overwrite the changes?'
				}),
				acceptButtonLabel: t('assets.detail.dataProcessingDefinition.overwriteConfirm', {
					defaultValue: 'Overwrite'
				}),
				declineButtonLabel: t('general.actions.cancel', {defaultValue: 'Cancel'})
			});

			if (overwrite) {
				// eslint-disable-next-line @typescript-eslint/no-unused-vars
				const {etag, ...updatedDefinitionWithoutEtag} = updatedDefinition;
				return await tryUpdateDpd(updatedDefinitionWithoutEtag, confirm, t);
			} else {
				return false;
			}
		} else {
			throw error;
		}
	}
};

export function createEmptyFormData(): KpiFormData {
	return {
		name: '',
		publishAsDatapointValues: true,
		flexExpression: '',
		inputVariables: [],
		outputVariables: [],
		processingOptions: {
			period: 'MIN15',
			startProcessingAt: new Date(),
			startProcessingAtHour: 15,
			ensureBoundaryValues: true
		}
	};
}

export function stringifyNameError(error: any, t: TFunction): string | undefined {
	const validationErrors = getDataProcessingDefinitionValidationErrors(error);
	if (
		validationErrors.length > 0 &&
		validationErrors.some((error) => error.validationErrorType === 'DATAPOINT_TYPE_NOT_CALCULATED_ERROR')
	) {
		return t('assets.detail.dataProcessingDefinition.edit.existingDatapointNameInGroup', {
			defaultValue:
				'This name is already in use by other datapoints within a group this definition is shared with. Please choose a different name ...'
		});
	}
}
